import { FC, createContext, useContext, useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BAND_ACTIONS, IBand, IBandSubscription } from "Bands/types";
import { CancelSubscriptionModal, EditCustomSubscriptionModal } from "Bands/containers";
import { StartTrialModal, EditTrialModal } from "Bands/pages/admin-dashboard/containers";
import { adminBandselector, dataMetaSelector, dispatchGetAdminBands } from "Bands/state";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { IAPIState } from "shared/types";

interface AdminContextState {
  bands: IBand[];
  fetchAdminBandsMeta?: IAPIState;
  fetchAdminBands: (page?: number, sortBy?: string, sortDirection?: string) => void;
  setEditTrialModal: (subscription: IBandSubscription | null) => void;
  setEditCustomModal: (subscription: IBandSubscription | null) => void;
  setCancelPlanModal: (subscription: IBandSubscription | null) => void;
  setStartTrialModalForBand: (bandId: string | null) => void;
}

const initialState: AdminContextState = {
  bands: [],
  fetchAdminBands: () => {},
  setEditTrialModal: () => {},
  setEditCustomModal: () => {},
  setStartTrialModalForBand: () => {},
  setCancelPlanModal: () => {},
};

const AdminContext = createContext<AdminContextState>(initialState);

export const AdminProvider: FC = ({ children }) => {
  const dispatch = useDispatch();

  const bands = useSelector(adminBandselector);

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.getAdminBands));

  useClearActionMetaOnUnMount(BAND_ACTIONS.getAdminBands);

  const [startTrialModalForBand, setStartTrialModalForBand] = useState<string | null>(null);
  const [editTrialModal, setEditTrialModal] = useState<IBandSubscription | null>(null);

  const [editCustomModal, setEditCustomModal] = useState<IBandSubscription | null>(null);

  const [cancelPlanModal, setCancelPlanModal] = useState<IBandSubscription | null>(null);

  const fetchAdminBands = useCallback(
    (page?: number, sortBy?: string, sortDirection?: string) => {
      dispatchGetAdminBands(dispatch, page, sortBy, sortDirection);
    },
    [dispatch]
  );

  useEffect(() => {
    fetchAdminBands();
  }, [dispatch, fetchAdminBands]);

  return (
    <AdminContext.Provider
      value={{
        bands,
        fetchAdminBandsMeta: apiMeta?.status,
        fetchAdminBands,
        setEditTrialModal,
        setEditCustomModal,
        setStartTrialModalForBand,
        setCancelPlanModal,
      }}
    >
      {children}
      {startTrialModalForBand && (
        <StartTrialModal
          bandId={startTrialModalForBand}
          handleClose={() => setStartTrialModalForBand(null)}
        />
      )}
      {editTrialModal && (
        <EditTrialModal trial={editTrialModal} handleClose={() => setEditTrialModal(null)} />
      )}
      {editCustomModal && (
        <EditCustomSubscriptionModal
          handleClose={() => setEditCustomModal(null)}
          subscription={editCustomModal}
        />
      )}
      {cancelPlanModal && (
        <CancelSubscriptionModal
          refetchBand
          subscription={cancelPlanModal}
          handleClose={() => setCancelPlanModal(null)}
        />
      )}
    </AdminContext.Provider>
  );
};

export const useAdminContext = () => useContext(AdminContext);
